import React from 'react';
import { Button, Card, EmptyState, Spinner, StatusBadge } from './ui';

const SortIcon = ({ state }) => (
  <span className={`ml-1 inline-flex flex-col leading-none ${state ? 'text-indigo-600' : 'text-slate-300'}`}>
    <svg className="h-2 w-2" viewBox="0 0 8 5" fill="currentColor" aria-hidden="true">
      <path d="M4 0l4 5H0z" opacity={state === 'desc' ? 0.3 : 1} />
    </svg>
    <svg className="h-2 w-2" viewBox="0 0 8 5" fill="currentColor" aria-hidden="true">
      <path d="M4 5L0 0h8z" opacity={state === 'asc' ? 0.3 : 1} />
    </svg>
  </span>
);

const SearchIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
  </svg>
);

/**
 * The shared list view: filter chips, search, sortable columns, row selection
 * with bulk actions, CSV export and pagination.
 *
 * `columns` entries are { key, label, sortable, className, render(row) }.
 */
const DataTable = ({
  title, description, icon,
  controller, columns, statuses,
  bulkStatuses, onRowClick,
  searchPlaceholder = 'Search…',
  emptyTitle = 'Nothing here yet',
  emptyDescription
}) => {
  const {
    rows, counts, pagination, loading, error,
    search, setSearch, status, setStatus,
    sort, toggleSort, setPage, limit, setLimit,
    selected, toggleRow, toggleAll, clearSelection,
    refresh, bulkAction, exportCsv
  } = controller;

  const allSelected = rows.length > 0 && selected.length === rows.length;
  const filterChips = ['all', ...statuses];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          {icon && (
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-xl">
              {icon}
            </div>
          )}
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">{title}</h1>
            {description && <p className="text-sm text-slate-500">{description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refresh} disabled={loading}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M20 9A8 8 0 006 5.3M4 15a8 8 0 0014 3.7" />
            </svg>
            Refresh
          </Button>
          <Button variant="primary" onClick={exportCsv} disabled={pagination.total === 0}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
            </svg>
            Export CSV
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {/* Filters */}
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {filterChips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => setStatus(chip)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition ${
                  status === chip
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {chip.replace(/-/g, ' ')}
                <span className={`ml-1.5 ${status === chip ? 'text-slate-300' : 'text-slate-400'}`}>
                  {counts[chip] ?? 0}
                </span>
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
              <SearchIcon />
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-xl border-0 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-b border-indigo-100 bg-indigo-50/70 px-4 py-3">
            <span className="text-sm font-medium text-indigo-900">
              {selected.length} selected
            </span>
            {bulkStatuses?.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-indigo-700">Set status</span>
                <select
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      bulkAction('status', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="rounded-lg border-0 bg-white py-1.5 pl-3 pr-8 text-sm capitalize text-slate-700 ring-1 ring-inset ring-indigo-200 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="" disabled>Choose…</option>
                  {bulkStatuses.map((s) => (
                    <option key={s} value={s} className="capitalize">{s.replace(/-/g, ' ')}</option>
                  ))}
                </select>
              </div>
            )}
            <Button
              variant="danger"
              className="py-1.5"
              onClick={() => {
                if (window.confirm(`Delete ${selected.length} record(s)? This cannot be undone.`)) {
                  bulkAction('delete');
                }
              }}
            >
              Delete
            </Button>
            <Button variant="ghost" className="py-1.5" onClick={clearSelection}>Clear</Button>
          </div>
        )}

        {/* Table */}
        {error ? (
          <EmptyState
            icon="⚠️"
            title="Could not load records"
            description={error}
            action={<Button variant="primary" onClick={refresh}>Try again</Button>}
          />
        ) : loading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-3 py-20 text-sm text-slate-500">
            <Spinner /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={search || status !== 'all' ? 'No matching records' : emptyTitle}
            description={
              search || status !== 'all'
                ? 'Try a different search term or clear the filters.'
                : emptyDescription
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th scope="col" className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all rows"
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 ${col.className || ''}`}
                    >
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key)}
                          // `uppercase` is repeated here because preflight resets
                          // text-transform on button elements.
                          className="inline-flex items-center uppercase tracking-wide transition hover:text-slate-900"
                        >
                          {col.label}
                          <SortIcon state={sort.sortBy === col.key ? sort.sortDir : null} />
                        </button>
                      ) : col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick?.(row)}
                    className={`transition hover:bg-slate-50 ${onRowClick ? 'cursor-pointer' : ''} ${
                      selected.includes(row.id) ? 'bg-indigo-50/50' : ''
                    }`}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleRow(row.id)}
                        aria-label={`Select row ${row.id}`}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className={`px-4 py-3 align-top text-sm ${col.className || ''}`}>
                        {col.render ? col.render(row) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {rows.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>
                {(pagination.page - 1) * pagination.limit + 1}
                –{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="rounded-lg border-0 bg-slate-50 py-1 pl-2 pr-7 text-xs text-slate-600 ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-indigo-500"
              >
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                className="py-1.5"
                disabled={pagination.page <= 1 || loading}
                onClick={() => setPage(pagination.page - 1)}
              >
                Previous
              </Button>
              <span className="px-1 text-sm text-slate-500">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button
                className="py-1.5"
                disabled={pagination.page >= pagination.pages || loading}
                onClick={() => setPage(pagination.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export { StatusBadge };
export default DataTable;
